import { defineComponent as _defineComponent } from 'vue'
import { getHoldHistoryBff } from "@/api/affiliate-balance";
import { useHold, HoldFilters, holdDefaultColumns } from "@/services/TablePage/hold";
import store from "@/store";
import { showServerError } from "@/utils";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";
import SvgOpenLink from "@/assets/icons/open-link.svg";
import { IHoldHistoryReport } from "@crm-ui/types";
import { FormattedItem } from "@/utils/CellsFromBff";

type TableItem = FormattedItem<IHoldHistoryReport>;

interface Props {
    constructorRef?: any;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'HoldTable',
  props: {
    constructorRef: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
    constructorRef?: any;
}



const { headers, summaryHeaders } = useHold();
const route = useRoute();
const router = useRouter();
const isLoading = ref(true);
const items = ref<TableItem[]>([]);
const summaryItems = ref<any[]>([]);
const total = ref(0);
const offset = ref(0);
const tableEl = ref(null);
const filterClass = new HoldFilters();

let loadListDebounceTimer: ReturnType<typeof setTimeout> | null = null;
/** Сигнатура последнего запроса — чтобы схлопывать дубли при множественных триггерах на маунте */
let lastRequestKey: string | null = null;

const getHeaders = computed(() => {
    const columns = (props.constructorRef?.selected! || route.query.columns) as string[];
    if (!columns || columns.length === 0) return headers;
    return headers.filter(h => h.fixed || columns.includes(h.value));
});

watch(() => route.query.columns, () => {
    getHoldList();
});

const hasHoldDateRange = (): boolean => {
    const q = route.query;
    const from = Array.isArray(q.date_from) ? q.date_from[0] : q.date_from;
    const to = Array.isArray(q.date_to) ? q.date_to[0] : q.date_to;
    if (from && to) return true;
    const p = store.getters['GET_FILTERS_PARAMS_OBJECT'] as Record<string, unknown>;
    return !!(p?.date_from && p?.date_to);
};

/** Колонки уже в URL — watch при маунте не сработает; после конструктора/стора догружаем один раз */
onMounted(() => {
    nextTick(() => {
        if (hasHoldDateRange()) {
            getHoldList();
        }
    });
});

onBeforeUnmount(() => {
    if (loadListDebounceTimer !== null) {
        clearTimeout(loadListDebounceTimer);
    }
});

const changePage = (offsetValue: number) => {
    offset.value = offsetValue;
    getHoldList();
};

const runGetHoldList = async (force = false): Promise<void> => {
    const params = getParams();
    const key = JSON.stringify(params);
    if (!force && key === lastRequestKey) return;
    lastRequestKey = key;
    try {
        isLoading.value = true;
        const { data, meta } = await getHoldHistoryBff(params);
        total.value = meta?.total!;
        items.value = data.report;
        summaryItems.value = data.summary;
    } catch (err) {
        showServerError(err, 'Список affiliate-balance не загружен');
    } finally {
        isLoading.value = false;
    }
};

/** Схлопывает повторные вызовы в один тик (маунт + router.replace по columns) */
const getHoldList = (force = false): void => {
    if (loadListDebounceTimer !== null) {
        clearTimeout(loadListDebounceTimer);
    }
    loadListDebounceTimer = setTimeout(() => {
        loadListDebounceTimer = null;
        void runGetHoldList(force);
    }, 0);
};

const resolveColumns = (): string[] => {
    const fromConstructor = props.constructorRef?.selected as string[] | undefined;
    let cols: string[];
    if (fromConstructor?.length) {
        cols = fromConstructor;
    } else {
        const q = route.query.columns;
        if (Array.isArray(q)) {
            const list = q.filter(Boolean) as string[];
            cols = list.length ? list : holdDefaultColumns;
        } else if (typeof q === 'string' && q.length) {
            cols = q.split(',').map((s) => s.trim()).filter(Boolean);
        } else {
            cols = holdDefaultColumns;
        }
    }
    return Array.from(new Set([...cols, ...SINGLE_QUERY_KEYS]));
};

const getParams = () => {
    const rawFilters = { ...store.getters['GET_FILTERS_PARAMS_OBJECT'] };
    const columns = resolveColumns();
    const filters = filterClass.prepareFiltersForSend(rawFilters);
    const paging = { limit: 100, offset: offset.value };
    return { filters, columns, paging };
};

const scrollToTop = () => {
    if (tableEl.value) {
        tableEl.value.scrollToTop();
    }
};

const SINGLE_QUERY_KEYS = ['offer_id', 'master_id', 'affiliate_id', 'advertiser_id', 'offer_currency'] as const;

/**
 * Фильтры отчёта, которые прокидываем в деталку.
 * Источник истины — GET_FILTERS_PARAMS_OBJECT (по нему же строится запрос списка),
 * а не route.query: дефолтная дата и неотправленные фильтры в урл не попадают.
 */
const HOLD_FILTER_QUERY_KEYS = [
    'date_from', 'date_to', 'offer_currency',
    'master_id', 'affiliate_id', 'affiliate_status',
    'master_manager_id', 'master_manager_department_id',
    'advertiser_id', 'advertiser_status', 'advertiser_manager_id',
    'offer_id', 'offer_status', 'offer_goal_id', 'offer_goal_status',
    'offer_department', 'offer_country', 'offer_product',
    'offer_target_action', 'offer_stats', 'offer_tag',
] as const;

const toQueryParam = (value: unknown): string | undefined => {
    if (value === null || value === undefined || value === '') return undefined;
    if (Array.isArray(value)) {
        const parts = value
            .filter((v) => v !== null && v !== undefined && v !== '')
            .map((v) => String(v));
        return parts.length ? parts.join(',') : undefined;
    }
    const str = String(value).trim();
    return str ? str : undefined;
};

const getFilterQueryFromStore = (): Record<string, string> => {
    const params = store.getters['GET_FILTERS_PARAMS_OBJECT'] as Record<string, unknown>;
    const query: Record<string, string> = {};
    for (const key of HOLD_FILTER_QUERY_KEYS) {
        const value = toQueryParam(params[key]);
        if (value !== undefined) query[key] = value;
    }
    return query;
};

const getDefaultQueryForSingle = (item: TableItem) =>
    Object.fromEntries(
        SINGLE_QUERY_KEYS
            .map(key => [key, item[key]?.componentData?.value])
            .filter(([, v]) => v != null),
    );

const goToSingle = (item: TableItem) => {
    const rowQuery = getDefaultQueryForSingle(item);
    // идентификаторы выбранной строки имеют приоритет над фильтрами отчёта
    const query = { ...getFilterQueryFromStore(), ...rowQuery };
    const path = router.resolve({ name: 'hold-single', query });
    window.open(path.href, '_blank');
};

const resetPage = (): void => {
    offset.value = 0;
    getHoldList();
};

expose({
    getHoldList,
    changePage,
    scrollToTop,
    resetPage,
});

return { __sfc: true,props, headers, summaryHeaders, route, router, isLoading, items, summaryItems, total, offset, tableEl, filterClass, loadListDebounceTimer, lastRequestKey, getHeaders, hasHoldDateRange, changePage, runGetHoldList, getHoldList, resolveColumns, getParams, scrollToTop, SINGLE_QUERY_KEYS, HOLD_FILTER_QUERY_KEYS, toQueryParam, getFilterQueryFromStore, getDefaultQueryForSingle, goToSingle, resetPage, BaseTable, BaseButtonV2, SvgOpenLink }
}

})